// ============================================================
// emailClientLayout.tsx
// Shared pieces for the two timed modals — ComposeEmailModal and
// AddActivityModal (both on ModalFrame xl + scrollBody): the SAME compact
// TimerControl in their footers and the SAME time/units helpers, so the two
// billing readouts can never diverge. (The old full-width BillingBar + the
// underlined field-row classes were retired with the ModalFrame redesign.)
// Purely presentational — all state + behaviour stays in the parent modals.
// ============================================================
import React from 'react'

// MM:SS clock readout from raw elapsed seconds. Defensive against negatives /
// non-integers. Shared so the two modals' clocks can never diverge.
export const secondsToClock = (total: number): string => {
  const s = Math.max(0, Math.floor(total))
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
}

// (Billing units: src/utils/billingUnits.ts — 6-minute units rounded UP, the
// same rule the server charges. The old completed-tenths display is gone.)

// ── TimerControl — the COMPACT billing control (approved email/activity
// mockup, Option A): the old full-width BillingBar consolidated into one pill
// that sits on the LEFT of the modal footer. Same behaviour + numbers as
// BillingBar (clock, Rec/Paused, Pause/Resume, Units override, "£ on save");
// Resume is MINT (one green primary per surface — the footer's Send/Save).
interface TimerControlProps {
  currentSeconds: number
  isTimerRunning: boolean
  onToggleTimer: () => void
  unitsInput: string
  onUnitsInputChange: (raw: string) => void
  disabled?: boolean
}
const TC_ICON = 'w-[15px] h-[15px] shrink-0'
export const TimerControl: React.FC<TimerControlProps> = ({
  currentSeconds,
  isTimerRunning,
  onToggleTimer,
  unitsInput,
  onUnitsInputChange,
  disabled = false,
}) => (
  <div
    role="group"
    aria-label="Billable time"
    className="flex items-center gap-[10px] text-[12.5px] text-casevo-text-soft bg-casevo-surface border border-casevo-border rounded-[10px] py-[6px] pl-[11px] pr-2"
  >
    <span className="inline-flex items-center gap-[7px]">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={`${TC_ICON} text-casevo-brand dark:text-[#8fd6b0]`} aria-hidden="true"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
      <b className="font-mono text-[16px] font-semibold text-casevo-forest tabular-nums" aria-label={`Time ${secondsToClock(currentSeconds)}`}>{secondsToClock(currentSeconds)}</b>
    </span>
    {isTimerRunning ? (
      <span className="inline-flex items-center gap-[5px] text-[10.5px] uppercase tracking-[0.12em] font-bold text-[#c23b2c] dark:text-[#f3a094]" aria-live="polite">
        <i className="w-[7px] h-[7px] rounded-full bg-[#e5484d] shadow-[0_0_0_3px_rgba(229,72,77,0.22)] animate-pulse motion-reduce:animate-none" aria-hidden="true" />Rec
      </span>
    ) : (
      <span className="text-[10.5px] uppercase tracking-[0.12em] font-bold text-casevo-text-soft">Paused</span>
    )}
    <button
      type="button"
      onClick={onToggleTimer}
      disabled={disabled}
      aria-label={isTimerRunning ? 'Pause timer' : 'Resume timer'}
      className={`inline-flex items-center gap-[5px] text-[12px] font-semibold rounded-[7px] px-2 py-[3px] border transition-colors focus:outline-none casevo-focus disabled:opacity-60 disabled:cursor-not-allowed ${
        isTimerRunning
          ? 'text-casevo-mid border-casevo-border hover:text-casevo-forest hover:border-casevo-mid'
          : 'bg-casevo-mint text-casevo-brand dark:text-[#8fd6b0] border-transparent hover:brightness-95'
      }`}
    >
      {isTimerRunning
        ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-[11px] h-[11px]" aria-hidden="true"><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></svg>
        : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinejoin="round" className="w-[11px] h-[11px]" aria-hidden="true"><polygon points="6 4 20 12 6 20 6 4" /></svg>}
      {isTimerRunning ? 'Pause' : 'Resume'}
    </button>
    <span className="w-px h-5 bg-casevo-border-soft" aria-hidden="true" />
    <label className="inline-flex items-center gap-[6px]" title="1 unit = 6 minutes, rounded up. Typing whole units here overrides the timer.">
      <span className="text-[10.5px] uppercase tracking-[0.14em] font-semibold">Units</span>
      <input
        type="number"
        inputMode="numeric"
        step="1"
        min="0"
        value={unitsInput}
        onChange={(e) => onUnitsInputChange(e.target.value)}
        disabled={disabled}
        aria-label="Time in units (manual override)"
        className="w-[52px] rounded-[6px] border border-casevo-border bg-casevo-input px-[7px] py-[2px] text-[13px] text-casevo-forest tabular-nums focus:outline-none focus:border-casevo-mid disabled:opacity-60 disabled:cursor-not-allowed"
      />
    </label>
    <span className="whitespace-nowrap tabular-nums" title="Charged at your rate when saved">£ on save</span>
  </div>
)
